import { useAuth } from 'auth/hooks/useAuth';
import { useCoreService } from 'core/services/useCoreService';
import { createContext, useEffect } from 'react';
import { FetchDataError, useDataFetcher } from 'shared/http';
import { useNotifications } from 'shared/notifications';
import { RefundRequest } from '../models/requests/RefundRequest';
import { RefundData } from '../models/RefundData';

const devolucionErrorMap = {
  amount_exceeds_max: 'El monto de la devolución excede el monto disponible de la cuenta',
  final_amount_is_zero: 'El monto de la devolución no puede ser cero',
  card_without_cc: 'La tarjeta no está asociada a ningun centro de costo',
};

interface IRefundsContext {
  refundData: RefundData;
  isLoading: boolean;
  isPartialRefund: boolean;
  doDevolucion: (amount: number) => void;
  tipoUsuario: string;
  error: FetchDataError | null;
}

export const RefundsContext = createContext<IRefundsContext>({
  refundData: null,
  tipoUsuario: null,
  isLoading: false,
  error: null,
  isPartialRefund: false,
  doDevolucion: () => { },
});

export const RefundsProvider = ({ children }) => {
  const [refundData, setRefundDataRequest] = useDataFetcher<RefundData>();
  const { user } = useAuth();
  const coreService = useCoreService();
  const { addNotification } = useNotifications();

  useEffect(() => {
    setRefundDataRequest(() => coreService.getCostCentreRefund());
  }, [setRefundDataRequest, coreService]);

  const contextValue = {
    refundData: refundData?.data || null,
    isLoading: refundData?.isLoading,
    error: refundData?.error || null,
    tipoUsuario: user?.tipoUsuario,
    attributes: user?.funcionalidades,
    isPartialRefund: user?.funcionalidades?.some(
      (fucionalidad) => fucionalidad === 'DEVOLUCION_PARCIAL',
    ),
    doDevolucion: (amount: number) => {
      setRefundDataRequest(() =>
        coreService
          .saveCostCentreRefund(
            new RefundRequest({
              centroCostoDestinoId: refundData?.data.centroCostoDestinoId,
              monto: amount,
            }),
          )
          .then(() => {
            addNotification('Devolución exitosa', 'success');
            return coreService.getCostCentreRefund();
          })
          .catch((err: FetchDataError) => {
            addNotification(devolucionErrorMap[err.apiError.message], 'error');
            return coreService.getCostCentreRefund();
          }),
      );
    },
  };
  return (
    <RefundsContext.Provider value={contextValue}>{children}</RefundsContext.Provider>
  );
};
